import { useCallback, useEffect, useState } from "react";
import {
  fetchAbnormalOrderSummary,
  fetchAbnormalEnrollmentOrders,
  fetchAbnormalGuestOrders,
  retryAbnormalEnrollmentOrderFinalize,
  retryAbnormalGuestOrderFinalize,
  type AbnormalEnrollmentOrderDto,
  type AbnormalGuestOrderDto,
} from "../../../api/platformAbnormalOrders";
import { getUserFacingMessage, showAlert } from "../../../api/errors";

type Tab = "guest" | "enrollment";

function formatChina(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", hour12: false });
}

function formatYuan(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

const ENROLLMENT_ANOMALY_LABEL: Record<string, string> = {
  no_application: "未关联申请单",
  application_missing: "申请单不存在",
  awaiting_payment: "申请仍为待支付",
};

const REVIEW_STATUS_LABEL: Record<string, string> = {
  awaiting_payment: "待支付",
  pending: "待审核",
  approved: "已通过",
  rejected: "已拒绝",
};

interface Props {
  onCountRefresh?: (count: number) => void;
}

export default function PlatformAbnormalOrders({ onCountRefresh }: Props) {
  const [tab, setTab] = useState<Tab>("guest");
  const [guestItems, setGuestItems] = useState<AbnormalGuestOrderDto[]>([]);
  const [enrollmentItems, setEnrollmentItems] = useState<AbnormalEnrollmentOrderDto[]>([]);
  const [guestTotal, setGuestTotal] = useState(0);
  const [enrollmentTotal, setEnrollmentTotal] = useState(0);
  const [summary, setSummary] = useState({ guestCount: 0, enrollmentCount: 0, count: 0 });
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  const pageSize = 20;

  const refreshSummary = useCallback(async () => {
    try {
      const s = await fetchAbnormalOrderSummary();
      setSummary(s);
      onCountRefresh?.(s.count);
    } catch {
      setSummary({ guestCount: 0, enrollmentCount: 0, count: 0 });
      onCountRefresh?.(0);
    }
  }, [onCountRefresh]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { keyword: keyword || undefined, page, pageSize };
      if (tab === "guest") {
        const res = await fetchAbnormalGuestOrders(params);
        setGuestItems(res.items);
        setGuestTotal(res.total);
      } else {
        const res = await fetchAbnormalEnrollmentOrders(params);
        setEnrollmentItems(res.items);
        setEnrollmentTotal(res.total);
      }
      await refreshSummary();
    } catch (e) {
      setError(getUserFacingMessage(e, "异常订单加载失败，请刷新页面。"));
      if (tab === "guest") {
        setGuestItems([]);
        setGuestTotal(0);
      } else {
        setEnrollmentItems([]);
        setEnrollmentTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [keyword, page, tab, refreshSummary]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const switchTab = (next: Tab) => {
    setTab(next);
    setPage(1);
    setKeyword("");
    setSearchInput("");
  };

  const handleSearch = () => {
    setPage(1);
    setKeyword(searchInput.trim());
  };

  const handleRetryGuest = async (row: AbnormalGuestOrderDto) => {
    if (retrying) return;
    const ok = window.confirm(
      `确认为租户「${row.tenantName}」的订单 ${row.outTradeNo} 执行补救？\n将尝试向微信查单并生成散客登记记录（无需用户再次付款）。`,
    );
    if (!ok) return;

    setRetrying(row.outTradeNo);
    try {
      const res = await retryAbnormalGuestOrderFinalize(row.outTradeNo);
      if (res.success) {
        showAlert(
          res.alreadyFinalized
            ? "该订单已完成登记，无需重复补救。"
            : `补救成功，登记编号：${res.guestRegistrationLegacyId ?? "—"}`,
        );
        await reload();
      } else {
        showAlert(res.failureReason || "补救未成功，请稍后重试或联系技术排查。");
      }
    } catch (e) {
      showAlert(getUserFacingMessage(e, "补救请求失败。"));
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryEnrollment = async (row: AbnormalEnrollmentOrderDto) => {
    if (retrying) return;
    const ok = window.confirm(
      `确认为租户「${row.tenantName}」的入会订单 ${row.outTradeNo} 执行补救？\n将尝试查单并把申请收口为「待审核」（无需用户再次付款）。`,
    );
    if (!ok) return;

    setRetrying(row.outTradeNo);
    try {
      const res = await retryAbnormalEnrollmentOrderFinalize(row.outTradeNo);
      if (res.success) {
        showAlert(
          `补救成功，申请单号：${res.applicationLegacyId ?? "—"}，状态：${
            REVIEW_STATUS_LABEL[res.reviewStatus ?? ""] ?? res.reviewStatus ?? "待审核"
          }`,
        );
        await reload();
      } else {
        showAlert(res.failureReason || "补救未成功，请稍后重试或联系技术排查。");
      }
    } catch (e) {
      showAlert(getUserFacingMessage(e, "补救请求失败。"));
    } finally {
      setRetrying(null);
    }
  };

  const total = tab === "guest" ? guestTotal : enrollmentTotal;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const searchPlaceholder =
    tab === "guest"
      ? "搜索租户名、单号、活动、姓名、手机…"
      : "搜索租户名、单号、申请单号、姓名、手机、等级…";

  return (
    <div>
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-error-warning-line text-amber-500"></i>
            异常订单
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            已支付但未完成业务收口的订单（散客登记 / 入会申请），可按租户查看并执行平台级补救。
          </p>
        </div>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={loading}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-amber-600 px-3 py-2 rounded-xl border border-gray-200 hover:border-amber-200 cursor-pointer disabled:opacity-50"
        >
          <i className="ri-refresh-line"></i>
          刷新
        </button>
      </div>

      <div className="flex gap-2 mb-4 border-b border-gray-100">
        <button
          type="button"
          onClick={() => switchTab("guest")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px cursor-pointer transition-colors ${
            tab === "guest"
              ? "border-amber-500 text-amber-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          散客登记
          {summary.guestCount > 0 ? (
            <span className="ml-1.5 text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
              {summary.guestCount}
            </span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={() => switchTab("enrollment")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px cursor-pointer transition-colors ${
            tab === "enrollment"
              ? "border-amber-500 text-amber-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          入会申请
          {summary.enrollmentCount > 0 ? (
            <span className="ml-1.5 text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
              {summary.enrollmentCount}
            </span>
          ) : null}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5 text-sm text-amber-900">
        <i className="ri-information-line mr-1"></i>
        {tab === "guest" ? (
          <>
            <strong>散客登记</strong>：微信已付，尚未生成登记记录。补救将查单并幂等创建登记。
          </>
        ) : (
          <>
            <strong>入会申请</strong>：线上微信已付，但申请仍为待支付或未关联申请单（不含线下支付/档案导入订单）。补救将查单并把申请推进为「待审核」。
          </>
        )}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder={searchPlaceholder}
          className="flex-1 min-w-[200px] border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="px-4 py-2 bg-amber-500 text-white text-sm rounded-xl hover:bg-amber-600 cursor-pointer"
        >
          搜索
        </button>
      </div>

      {error ? (
        <div className="bg-red-50 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>
      ) : null}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          {tab === "guest" ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-left">
                  <th className="px-4 py-3 font-medium whitespace-nowrap">租户</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">活动</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">登记人</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">金额</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">支付时间</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">商户单号</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                      <i className="ri-loader-4-line animate-spin mr-1"></i>
                      加载中…
                    </td>
                  </tr>
                ) : guestItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                      {keyword ? "无匹配异常订单" : "暂无散客登记异常订单"}
                    </td>
                  </tr>
                ) : (
                  guestItems.map((row) => (
                    <tr key={row.outTradeNo} className="border-t border-gray-50 hover:bg-gray-50/80">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{row.tenantName}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{row.tenantLegacyId}</p>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <p className="text-gray-800 truncate" title={row.activityTitle}>
                          {row.activityTitle || "—"}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{row.sessionLegacyId}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-800">{row.guestName || "—"}</p>
                        <p className="text-xs text-gray-500">{row.guestPhone || "—"}</p>
                        {row.idNumber ? (
                          <p className="text-xs text-gray-400 mt-0.5">{row.idNumber}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">
                        {formatYuan(row.amountFen)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {formatChina(row.paidAt || row.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-mono text-xs text-gray-700 break-all">{row.outTradeNo}</p>
                        {row.wxTransactionId ? (
                          <p className="font-mono text-xs text-gray-400 mt-0.5 break-all">{row.wxTransactionId}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={retrying === row.outTradeNo}
                          onClick={() => void handleRetryGuest(row)}
                          className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 cursor-pointer disabled:opacity-50"
                        >
                          {retrying === row.outTradeNo ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              处理中
                            </>
                          ) : (
                            <>
                              <i className="ri-first-aid-kit-line"></i>
                              补救收口
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-left">
                  <th className="px-4 py-3 font-medium whitespace-nowrap">租户</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">申请人</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">等级</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">异常原因</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">金额</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">支付时间</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">商户单号</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                      <i className="ri-loader-4-line animate-spin mr-1"></i>
                      加载中…
                    </td>
                  </tr>
                ) : enrollmentItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                      {keyword ? "无匹配异常订单" : "暂无入会申请异常订单"}
                    </td>
                  </tr>
                ) : (
                  enrollmentItems.map((row) => (
                    <tr key={row.outTradeNo} className="border-t border-gray-50 hover:bg-gray-50/80">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{row.tenantName}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{row.tenantLegacyId}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-800">{row.applicantName || "—"}</p>
                        <p className="text-xs text-gray-500">{row.applicantPhone || "—"}</p>
                        {row.applicationLegacyId ? (
                          <p className="text-xs text-gray-400 mt-0.5 font-mono">{row.applicationLegacyId}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{row.levelLabel || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">
                          {ENROLLMENT_ANOMALY_LABEL[row.anomalyReason] ?? row.anomalyReason}
                        </span>
                        {row.applicationReviewStatus ? (
                          <p className="text-xs text-gray-400 mt-1">
                            申请状态：
                            {REVIEW_STATUS_LABEL[row.applicationReviewStatus] ?? row.applicationReviewStatus}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">
                        {formatYuan(row.amountFen)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {formatChina(row.paidAt || row.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-mono text-xs text-gray-700 break-all">{row.outTradeNo}</p>
                        {row.wxTransactionId ? (
                          <p className="font-mono text-xs text-gray-400 mt-0.5 break-all">{row.wxTransactionId}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={retrying === row.outTradeNo}
                          onClick={() => void handleRetryEnrollment(row)}
                          className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 cursor-pointer disabled:opacity-50"
                        >
                          {retrying === row.outTradeNo ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              处理中
                            </>
                          ) : (
                            <>
                              <i className="ri-first-aid-kit-line"></i>
                              补救收口
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {total > pageSize ? (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
            <span>
              共 {total} 条，第 {page} / {totalPages} 页
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 rounded-lg border border-gray-200 disabled:opacity-40 cursor-pointer hover:bg-gray-50"
              >
                上一页
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded-lg border border-gray-200 disabled:opacity-40 cursor-pointer hover:bg-gray-50"
              >
                下一页
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
